import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import logger from '../../../utils/logger';
import { archiveService } from '../../../services/archiveService';
import { useAlert } from '../../../hooks/useAlert';
import { useAuth } from '../../../contexts/AuthContext';
import { adminService } from '../../../services/adminService';
import { usePermissions } from '../../../hooks/usePermissions';
import { EMPTY_ENRICHMENT, enrichmentFor, loadHistoryEnrichment, snapshotOnDay, toCsv, contractKey, plateMatchKey, searchedPlate, INVYGO_IDS_FAILED } from './historyLens';
import Button from '../../../ui/Button';
import Icon from '../../../ui/Icon';
import { ICONS } from '../../../ui/icon-map';
import Card from '../../../ui/Card';
import { CellId, CellText, CellPlate, CellMuted, CellStatus } from '../../../ui/DataTable/cells';
import Dialog from '../../../ui/Dialog/Dialog';
import DataTable from '../../../ui/DataTable/DataTable';
import { Field, Input, Select, FormGrid } from '../../../ui/Field/Field';
import { useContacts } from '../../../components/ContactPhone';
import { contactsService, formatPhone, phoneDigits } from '../../../services/contactsService';
import { newestFirst, echeckGapShown } from '../../../services/ejarOpenClose';

// the E-Check's own time under Ejar's: the time alone on the same day ("13:45"), the whole date-time on another
const echeckShort = (row) => {
    const [day, time] = String(row['E-Check Time'] || '').split(' ');
    return day && day === String(row['Transaction Date'] || '').split(' ')[0] ? time : `${day || ''} ${time || ''}`.trim();
};

export const ArchiveSearch = () => {
    // the CSV carries names and phones: its own permission (permissions phase 4, 4 Oct 2026)
    const canExport = usePermissions().hasPermission('export_history');
    const contactsReady = useContacts();
    const [searchTerm, setSearchTerm] = useState("");
    const [results, setResults] = useState([]);
    const [fetchedTerm, setFetchedTerm] = useState('');   // the term the results were read for
    const [error, setError] = useState(null);

    // Filter states
    const [filterBranch, setFilterBranch] = useState("All");
    const [filterType, setFilterType] = useState("All");
    const [filterHasDamages, setFilterHasDamages] = useState("All");
    const [filterDate, setFilterDate] = useState(""); // YYYY-MM-DD

    // Modal state
    const [selectedRecord, setSelectedRecord] = useState(null);

    const { currentUser } = useAuth();
    const { alertError } = useAlert();
    const [searchParams, setSearchParams] = useSearchParams();

    // Customer / booking details of the contracts found (read by id for the results only - CON-07)
    const [enrichment, setEnrichment] = useState(EMPTY_ENRICHMENT);
    const [enrichError, setEnrichError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    const handleReset = () => {
        setSearchTerm("");
        setFilterDate("");
        setFilterBranch("All");
        setFilterType("All");
        setFilterHasDamages("All");
        setResults([]);
        setHasSearched(false);
        setError(null);
        setSearchParams({}); // Clear URL params to fix stickiness
        localStorage.removeItem('archiveSearch_data');
    };

    // Read the details of the contracts found - only those, never the whole collections on open.
    useEffect(() => {
        const ids = [...new Set(results.map((r) => contractKey(r['Contract No.'])).filter(Boolean))];
        if (!ids.length) { setEnrichment(EMPTY_ENRICHMENT); setEnrichError(null); return undefined; }
        let alive = true;
        loadHistoryEnrichment(ids)
            // only the Invygo ids failed: the names / phones / bookings that loaded are shown, and that is said
            .then(({ index, invygoError }) => { if (alive) { setEnrichment(index); setEnrichError(invygoError ? INVYGO_IDS_FAILED : null); } })
            .catch((e) => {
                logger.error('History Lens: customer details not loaded', e);
                if (alive) setEnrichError('Customer and booking details could not be loaded - names below are from the E-Check archive only. Fetch Records again to retry.');
            });
        return () => { alive = false; };
    }, [results]);

    // Derived enriched results
    const enrichedResults = useMemo(() => {
        return results.map(row => {
            const contractNo = contractKey(row['Contract No.']);
            const { airportMatch, ejarMatch, invygoMatch } = enrichmentFor(enrichment, contractNo);

            const finalName = airportMatch?.Customer || airportMatch?.['Customer Name'] || ejarMatch?.Customer || invygoMatch?.customerName || row['Customer Name'] || 'Unknown';
            const contact = contactsReady ? contactsService.peek(contractNo) : null;
            // one format with the country code whatever the source (27 Sep 2026)
            const finalPhone = formatPhone(phoneDigits((contact && contact.p) || airportMatch?.['Phone Number'] || airportMatch?.Phone || ejarMatch?.['Phone Number'] || invygoMatch?.phone || row['Phone Number'])) || 'N/A';
            // Invygo's own booking id first (invygoIds: Ejar's broker reference, or the old sheet's history)
            const finalBooking = invygoMatch?.invygoId || airportMatch?.booking_number || ejarMatch?.['Booking Number'] || 'N/A';
            const source = airportMatch ? 'Airport' : ejarMatch ? 'EJAR' : invygoMatch ? 'Invygo' : 'Archive';

            return {
                ...row,
                'Customer Name': finalName,
                'Phone Number': finalPhone,
                'Booking Search': finalBooking,
                'EnrichedBy': source
            };
        });
    }, [results, enrichment, contactsReady]);

    // Derived filtered results for the table
    // The Action Date applies at once to the rows on screen (it used to wait for Fetch Records - CON-20).
    const filteredResults = useMemo(() => {
        const onDay = filterDate ? snapshotOnDay(enrichedResults, filterDate) : enrichedResults;
        // a car searched with a day: only the contracts that had THAT car on the day - one that held it a minute and
        // drove another car that day is not this car's (owner, 3 Oct 2026, CC 79361)
        const plate = filterDate ? searchedPlate(fetchedTerm, results) : '';
        const ofCar = plate ? onDay.filter((row) => plateMatchKey(row['Plate Number']) === plate) : onDay;
        // newest first (owner, 6 Oct 2026: "الارشيف المفروض يظهر الحديث فى الاول")
        return newestFirst(ofCar.filter(row => {
            const branchMatch = filterBranch === "All" || row['Branch'] === filterBranch;
            const typeMatch = filterType === "All" || row['Check Type'] === filterType;
            let damageMatch = true;
            if (filterHasDamages !== "All") {
                // a row from Ejar alone had no E-Check: no damage audit either way
                if (row._ejarOnly) return false;
                const hasDam = row['Has Damages'] === 'Exist' || row['Has Damages'] === 'Yes' || row['Has Damages'] === true;
                damageMatch = filterHasDamages === "Yes" ? hasDam : !hasDam;
            }
            return branchMatch && typeMatch && damageMatch;
        }));
    }, [enrichedResults, filterDate, filterBranch, filterType, filterHasDamages, fetchedTerm, results]);

    const branches = useMemo(() => ["All", ...new Set(results.map(r => r['Branch']).filter(Boolean))], [results]);
    const checkTypes = useMemo(() => ["All", ...new Set(results.map(r => r['Check Type']).filter(Boolean))], [results]);

    useEffect(() => {
        const urlQuery = searchParams.get('search');
        if (urlQuery) {
            setSearchTerm(urlQuery);
        }
        // The page always opens clean: no remembered search or customer (and forget one saved before)
        try { localStorage.removeItem('archiveSearch_data'); } catch (e) { /* storage blocked */ }
    }, [searchParams]);

    useEffect(() => {
        const urlQuery = searchParams.get('search');
        if (urlQuery && urlQuery !== "" && !loading && !hasSearched) {
            handleSearch(urlQuery);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const handleSearch = async (term = searchTerm) => {
        if (!term) {
            alertError("Please enter a search term.", "Missing Input");
            return;
        }

        setLoading(true);
        setError(null);
        try {
            // The records of the term and the whole life of every contract they belong to, each contract opened and
            // closed by the owner's rule - Ejar's open first, the close whichever is first, a contract with no E-Check
            // yet from Ejar alone (archiveService.historySearch). (The Action Date is applied on screen.)
            const res = await archiveService.historySearch(term);

            setResults(res);
            setFetchedTerm(term);
            setHasSearched(true);
            adminService.logUserAction('SEARCH_ARCHIVE', `Searched Archive for "${term || filterDate}"`, currentUser, { query: term, date: filterDate });
        } catch (err) {
            setError("Failed to load archive data. Please check connection.");
            logger.error(err);
        } finally {
            setLoading(false);
        }
    };

    // The export has the table's columns (and the phone of the details), CSV-quoted: a name with a
    // comma no longer shifts the row (CON-20).
    const exportToCSV = () => {
        if (filteredResults.length === 0) return;

        const headers = ["Customer", "Contract No.", "Booking", "Phone", "Plate Number", "Model", "Branch", "Timestamp", "Type", "Damages"];
        const rows = filteredResults.map(r => [
            r['Customer Name'],
            r['Contract No.'],
            r['Booking Search'],
            r['Phone Number'],
            r['Plate Number'],
            r['Model'],
            r['Branch'],
            r['Transaction Date'],
            r['Check Type'],
            r['Has Damages']
        ]);

        const blob = new Blob(['\ufeff' + toCsv(headers, rows)], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `Archive_Export_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    const columns = [
        {
            key: 'Customer Name',
            header: 'Customer',
            cell: (row) => (
                <div className="flex flex-col items-center gap-0.5">
                    <CellText>{row['Customer Name'] || 'Unknown Customer'}</CellText>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(row['Contract No.']);
                            window.open('https://ejarprod.iyelo.com:6300//app/rental/contracts', '_blank');
                        }}
                        className="hover:underline w-fit"
                        title="Click to copy and open in Renty"
                    >
                        <CellId>{row['Contract No.']}</CellId>
                    </button>
                </div>
            ),
            searchable: false,
        },
        {
            key: 'Booking Search',
            header: 'Booking',
            cell: (row) => (row['Booking Search'] && row['Booking Search'] !== 'N/A' ? (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(row['Booking Search']);
                        window.open('https://dashboard.invygo.com/bookings', '_blank');
                    }}
                    className="hover:underline"
                    title="Click to copy and open Invygo"
                >
                    <CellId>{row['Booking Search']}</CellId>
                </button>
            ) : (
                <CellMuted>{row['Booking Search'] || 'N/A'}</CellMuted>
            )),
        },
        {
            key: 'Plate Number',
            header: 'Vehicle',
            cell: (row) => (
                <div>
                    <CellPlate>{row['Plate Number']}</CellPlate>
                    <CellMuted>{row['Model'] || 'N/A'}</CellMuted>
                </div>
            ),
        },
        {
            key: 'Branch',
            header: 'Branch',
            cell: (row) => <CellText>{row['Branch']}</CellText>,
        },
        {
            key: 'Transaction Date',
            header: 'Timestamp',
            // Ejar's time when it came first; the E-Check's own time under it when more than an hour apart (owner,
            // 6 Oct 2026); a row from Ejar alone says so
            cell: (row) => (
                <div className="flex flex-col items-center">
                    <span className="ds-cell-date">{row['Transaction Date']}</span>
                    {echeckGapShown(row) && (
                        <CellMuted>{row._ejarOnly ? 'Ejar · no E-Check' : `Ejar · E-Check ${echeckShort(row)}`}</CellMuted>
                    )}
                </div>
            ),
            sortable: false,
        },
        {
            key: 'status',
            header: 'Status',
            accessor: (row) => row['Check Type'],
            cell: (row) => {
                const isOpening = row['Check Type']?.toLowerCase().includes('open') || row['Check Type']?.toLowerCase().includes('create');
                const isDam = row['Has Damages'] === 'Exist' || row['Has Damages'] === 'Yes' || row['Has Damages'] === true;
                const type = (row['Check Type'] || 'LOG').toUpperCase();
                return (
                    <div className="flex flex-wrap items-center gap-1.5">
                        <CellStatus status={type} label={type} tone={isOpening ? 'success' : 'danger'} />
                        {row._ejarOnly ? null : isDam ? (
                            <CellStatus status="Damage" label="Damage" tone="warning" />
                        ) : (
                            <CellStatus status="Clear" label="Clear" tone="success" />
                        )}
                    </div>
                );
            },
        },
    ];

    return (
        <div className="space-y-4 pb-20">
            {/* SEARCH BOX */}
            <Card
                title="Archive Explorer"
                subtitle="System records & logs"
                actions={hasSearched && canExport ? (
                    <Button onClick={exportToCSV} variant="secondary" size="sm" leadingIcon={<Icon glyph={ICONS.export} size="sm" />}>
                        Export Data
                    </Button>
                ) : null}
            >
                <div className="space-y-4">
                    <div className="flex flex-col md:flex-row gap-2">
                        <Input
                            type="text"
                            aria-label="Search archive"
                            placeholder="Search by Plate Number, Contract No, or Reference..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                            prefix={<Icon glyph={ICONS.search} size="sm" />}
                            className="flex-1"
                        />
                        <Button
                            variant="primary"
                            onClick={() => handleSearch()}
                            disabled={loading}
                            isLoading={loading}
                        >
                            {loading ? "Searching..." : "Fetch Records"}
                        </Button>
                        {hasSearched && (
                            <Button onClick={handleReset} variant="ghost">
                                Clear Explorer
                            </Button>
                        )}
                    </div>

                    {hasSearched && results.length > 0 && (
                        <FormGrid cols={4} className="pt-4 border-t border-gray-100">
                            <Field label="Pickup Branch">
                                <Select value={filterBranch} onChange={(e) => setFilterBranch(e.target.value)} size="sm">
                                    {branches.map(b => <option key={b} value={b}>{b}</option>)}
                                </Select>
                            </Field>
                            <Field label="Operation Type">
                                <Select value={filterType} onChange={(e) => setFilterType(e.target.value)} size="sm">
                                    {checkTypes.map(t => <option key={t} value={t}>{t}</option>)}
                                </Select>
                            </Field>
                            <Field label="Action Date">
                                <Input type="date" size="sm" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} />
                            </Field>
                            <Field label="Damage Status">
                                <Select value={filterHasDamages} onChange={(e) => setFilterHasDamages(e.target.value)} size="sm">
                                    <option value="All">Show All Transactions</option>
                                    <option value="Yes">Only Damaged</option>
                                    <option value="No">Only Clear</option>
                                </Select>
                            </Field>
                        </FormGrid>
                    )}
                </div>
            </Card>

            {(error || enrichError) && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-3" role="alert">
                    <Icon glyph={ICONS.warning} size="sm" />
                    <div className="font-medium text-sm">{error || enrichError}</div>
                </div>
            )}

            {hasSearched && (
                <DataTable
                    title="Results"
                    description={`Matches: ${filteredResults.length}`}
                    columns={columns}
                    data={filteredResults}
                    rowKey={(row, idx) => idx}
                    loading={loading}
                    searchable={false}
                    onRowClick={(row) => setSelectedRecord(row)}
                    emptyTitle="No records found"
                    emptyDescription="We couldn't find any transaction matching your query. Please double-check your search term or filters."
                    emptyAction={(
                        <Button onClick={handleReset} variant="secondary" size="sm">
                            Reset search filters
                        </Button>
                    )}
                />
            )}

            {/* DETAIL MODAL */}
            <Dialog
                open={Boolean(selectedRecord)}
                onClose={() => setSelectedRecord(null)}
                title={selectedRecord ? selectedRecord['Contract No.'] : ''}
                description={selectedRecord ? `${selectedRecord['Customer Name'] || ''} · ${(selectedRecord['EnrichedBy'] || 'Archive')} sourced` : ''}
                size="lg"
                footer={(
                    <Button variant="primary" onClick={() => setSelectedRecord(null)}>
                        Close details
                    </Button>
                )}
            >
                {selectedRecord && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <DetailBox label="Plate Registration" value={selectedRecord['Plate Number']} icon={ICONS.car} />
                        <DetailBox label="Booking ID" value={selectedRecord['Booking Search']} icon={ICONS.booking} />
                        <DetailBox label="Vehicle Information" value={selectedRecord['Model']} icon={ICONS.fleet} />
                        <DetailBox label="Current Location" value={selectedRecord['Branch']} icon={ICONS.home} />
                        <DetailBox label="Log Timestamp" value={selectedRecord['Transaction Date']} icon={ICONS.clock} />
                        <DetailBox label="Operation Task" value={selectedRecord['Check Type']} icon={ICONS.operations} />
                        <DetailBox label="Damage Audit" value={selectedRecord['Has Damages']} icon={ICONS.warning} />
                        <DetailBox label="Communication" value={selectedRecord['Phone Number']} icon={ICONS.phone} />
                        <DetailBox label="Data Source" value={(selectedRecord['EnrichedBy'] || 'Archive')} icon={ICONS.database} />
                    </div>
                )}
            </Dialog>
        </div>
    );
};

const DetailBox = ({ label, value, icon }) => (
    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <div className="flex items-center gap-2 mb-1 text-gray-500">
            <Icon glyph={icon} size="sm" />
            <span className="text-xs font-medium">{label}</span>
        </div>
        <div className="text-sm font-medium text-gray-900 truncate">{value || 'Not Logged'}</div>
    </div>
);

export default ArchiveSearch;
